import { InputAdornment, SxProps, useMediaQuery, useTheme } from '@mui/material';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import React, { forwardRef, ReactElement } from 'react';
import zeplinColor from '../../../theme/Color';
import { CustomTextMaskInput } from './CustomTextMaskInput';

type CustomTextFieldType = {
  style?: React.CSSProperties | SxProps | undefined;
  text: string;
  error?: string | boolean;
  label: string;
  type?: string;
  name: string;
  onBlur?: () => void;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disable?: boolean;
  required?: boolean;
  tabindex?: number;
  multiline?: boolean;
  startIcon?: boolean;
  rows?: number;
  close?: boolean;
  handleClear?: () => void;
  endIcon?: ReactElement;
  max?: number;
  placeHolder?: string;
  onEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  form?: boolean;
  isPhoneMask?: boolean;
};

const CustomField = styled(TextField)(() => ({
  '& .MuiFormHelperText-root.Mui-error ': {
    marginTop: '0px',
    height: '0px',
    marginBottom: '1.5rem',
  },
  '& .MuiFormLabel-asterisk': {
    color: zeplinColor.Error,
  },
}));

const CustomTextField = forwardRef<HTMLDivElement, CustomTextFieldType>(
  (
    {
      style,
      text,
      error,
      label,
      type = 'text',
      onBlur,
      onChange,
      name,
      disable = false,
      required = false,
      tabindex,
      multiline = false,
      close = false,
      handleClear,
      endIcon,
      startIcon,
      max,
      placeHolder = '',
      onEnter,
      form = false,
      isPhoneMask = false,
      rows,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down('xl'));
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.code === 'Enter' && onEnter) {
        onEnter(e);
      }
    };

    return (
      <>
        <CustomField
          onClick={(e) => {
            e.stopPropagation();
          }}
          ref={ref}
          required={required}
          disabled={disable}
          onKeyDown={handleKeyDown}
          name={name}
          size={isXs && form ? 'small' : 'medium'}
          onBlur={onBlur}
          label={label}
          type={type}
          placeholder={placeHolder ? placeHolder : label}
          error={error ? true : false}
          helperText={error ? error : ''}
          sx={style ? { ...style } : {}}
          value={text}
          onChange={(e: any) => {
            let val = +e.target.value;
            if (type === 'number' && max && val > max) {
              return;
            }
            onChange(e);
          }}
          tabIndex={tabindex}
          multiline={multiline}
          rows={rows}
          slotProps={{
            inputLabel: {
              size: isXs && form ? 'small' : 'medium',
              sx: { fontSize: isXs && form ? 14 : 16 },
            },
          }}
          InputProps={{
            startAdornment:
              label === 'Rate' || label === 'Sales price' || label === 'Amount' ? (
                <InputAdornment position='start' sx={{ '& p': { color: zeplinColor.BasicBlack } }}>
                  $
                </InputAdornment>
              ) : startIcon ? (
                <InputAdornment position='start' sx={{ '& p': { color: zeplinColor.BasicBlack } }}>
                  <img
                    width={20}
                    height={20}
                    src={'/assets/clear-input.svg'}
                    alt='clear-input'
                    style={{
                      borderRadius: '14px',
                      objectFit: 'fill',
                      cursor: 'pointer',
                    }}
                  />
                </InputAdornment>
              ) : (
                ''
              ),
            endAdornment: endIcon
              ? endIcon
              : close && (
                  <InputAdornment position='end'>
                    <img
                      width={20}
                      height={20}
                      src={'/assets/clear-input.svg'}
                      alt='clear-input'
                      style={{
                        borderRadius: '14px',
                        objectFit: 'fill',
                        cursor: 'pointer',
                      }}
                      onClick={handleClear}
                    />
                  </InputAdornment>
                ),
            inputProps: {
              ...(type === 'number'
                ? {
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    min: 0,
                    max: max,

                    onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === '+' || e.key === '-') {
                        e.preventDefault();
                      }
                    },
                  }
                : {}),
              type: type,
            },
            inputComponent: isPhoneMask ? (CustomTextMaskInput as any) : undefined,
            sx: {
              '&.MuiOutlinedInput-root': {
                fontSize: isXs && form ? 14 : 16,
              },
              '& input::placeholder': {
                fontSize: isXs && form ? 14 : 16,
              },
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                display: 'none',
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            },
          }}
        />
      </>
    );
  },
);

export default CustomTextField;
